import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { PasswordInput, AuthSubmitButton, AuthPageLayout, AuthCard, AuthHelpText } from './components';
import { validatePassword, validatePasswordConfirm } from '../../utils/validation';
import { LoadingSpinner } from '../common';

const SetupPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!token) {
      toast.error('Invalid invitation link');
      navigate('/login');
      return;
    }

    // Verify the invitation token
    const verifyToken = async () => {
      try {
        const response = await axios.get(`/api/auth/verify-invitation/${token}`);
        setUserInfo(response.data.user);
        setVerifying(false);
      } catch (error) {
        toast.error('Invalid or expired invitation link');
        navigate('/login');
      }
    };

    verifyToken();
  }, [token, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error for this field
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      newErrors.password = passwordError;
    }

    const confirmError = validatePasswordConfirm(formData.password, formData.confirmPassword);
    if (confirmError) {
      newErrors.confirmPassword = confirmError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post('/api/auth/setup-password', {
        token,
        password: formData.password
      });

      if (response.data.token) {
        // Store token and user data
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        toast.success('Password set successfully! Welcome to FitFlow!');
        
        // Login the user
        await login(response.data.user, response.data.token);
        
        // Navigate to dashboard
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error setting up password');
    } finally {
      setLoading(false);
    }
  };

  // Loading state - verifying token
  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Verifying invitation...</p>
        </div>
      </div>
    );
  }

  // Invalid invitation state
  if (!userInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-red-600 text-xl font-semibold mb-2">Invalid Invitation</div>
          <p className="text-gray-600">This invitation link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  // Lock icon for setup password
  const lockIcon = (
    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );

  return (
    <AuthPageLayout
      title="Complete Your Account"
      subtitle={`Welcome ${userInfo.firstName}! Set up your password to get started.`}
      icon={lockIcon}
    >
      <AuthCard>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* User Info Display */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6">
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p><strong>Name:</strong> {userInfo.firstName} {userInfo.lastName}</p>
              <p><strong>Email:</strong> {userInfo.email}</p>
            </div>
          </div>

          {/* Password Field */}
          <PasswordInput
            id="password"
            name="password"
            label="Password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Enter your password"
            autoComplete="new-password"
            error={errors.password}
          />

          {/* Confirm Password Field */}
          <PasswordInput
            id="confirmPassword"
            name="confirmPassword"
            label="Confirm Password"
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder="Confirm your password"
            autoComplete="new-password"
            error={errors.confirmPassword}
          />

          {/* Submit Button */}
          <AuthSubmitButton 
            loading={loading} 
            loadingText="Setting up account..."
            className="mt-6"
          >
            Complete Setup
          </AuthSubmitButton>
        </form>

        <AuthHelpText />
      </AuthCard>
    </AuthPageLayout>
  );
};

export default SetupPassword;
